/**
 * BarraComandi — Ctrl K (Cmd K sul Mac), da ogni schermata del gestionale
 * (stile «Console», fase 1, punto 5; mockup «Comandi»).
 *
 * PROPS (le passa il Layout, che tiene lo stato: cosi' la puo' aprire
 * anche un pulsante della barra in alto)
 *   utente     l'utente collegato (useAuthStore().user)
 *   aperta     true se la barra e' aperta
 *   onApri()   la apre
 *   onChiudi() la chiude
 *
 * CHE COSA OFFRE, in tre parti e da nessun'altra parte:
 *   «Azioni»    azioniDi(utente) della tabella delle schermate: si aprono
 *               andando alla pagina con lo stato { azione }, e la pagina
 *               apre il suo modulo col gesto del suo pulsante (useAzione);
 *   «Vai a»     vociVaiA(utente): etichetta, gruppo, e `cerca` per i nomi
 *               di prima («Fatturazione» trova «Fatture»). Gia' filtrate
 *               per ruolo con la stessa funzione delle rotte: qui si
 *               filtra solo per il testo scritto (navigazione/comandi.js);
 *   «Pazienti»  la ricerca della pagina Pazienti, pazientiAPI.getAll, che
 *               il server protegge e annota nel registro degli accessi come
 *               ogni ricerca della pagina. Aprire un paziente e'
 *               navigate(`/pazienti/${id}`), la strada di un clic
 *               nell'elenco: la stessa rotta del server, quindi la stessa
 *               traccia. Nel risultato solo cio' che l'elenco gia' mostra
 *               (nome, cognome, codice del paziente). La parte c'e' solo
 *               per chi la cartella la puo' aprire.
 *               SE IL TESTO E' IL NOME DI UNA SCHERMATA (nomeDiSchermata:
 *               «fatture», «reg mod») la ricerca non parte da sola: c'e' la
 *               voce «Cerca fra i pazienti», e si cerca solo scegliendola.
 *               Chi scrive il nome di una schermata vuole andarci, e una
 *               ricerca partita da sola restava nel registro degli accessi
 *               come una ricerca di pazienti (2/10/2026).
 * Una schermata che la tabella non offre all'utente non si offre: la prova
 * e' scripts/verificaNavigazione.js (nel prebuild),
 * src/navigazione/navigazione.test.js e src/navigazione/barraComandi.test.js;
 * sul banco, scripts/provaBarraComandi.js.
 *
 * DOVE NON SI APRE. Sta nel Layout, quindi non esiste dove il Layout non
 * c'e': il tablet in sala d'attesa, la timbratura, il login, la pagina del
 * telefono. E non ruba Ctrl K al campo che lo usa (campoTieneCtrlK in
 * navigazione/comandi.js): chi lo gestisce da se', chi lo dichiara con
 * data-tiene-ctrl-k, e sul Mac i campi di testo, dove Ctrl K e' del
 * sistema.
 *
 * LA TASTIERA. Su e giu' scorrono le voci (in fondo si riparte da capo),
 * Invio apre quella scelta, Esc chiude e rimette il cursore dov'era. Il
 * cursore resta sempre nel campo di ricerca (aria-activedescendant): si
 * continua a scrivere senza dover tornare indietro.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, Plus, ArrowRight, UserSearch } from 'lucide-react';
import { pazientiAPI } from '../../services/api';
import { messaggioDa } from '../../utils/notifiche';
import { vociVaiA, azioniDi, schermataDi, siApre } from '../../navigazione/schermate';
import {
    vociComandi, vocePaziente, indiceMigliore, eCtrlK, campoTieneCtrlK, eMac,
    nomeDiSchermata, voceCercaPazienti, MINIMO_PAZIENTI, LIMITE_PAZIENTI,
} from '../../navigazione/comandi';
import { apriDestinazione } from '../../navigazione/useAzione';
import '../../styles/BarraComandi.css';

/* L'attesa dopo l'ultimo tasto prima di cercare un paziente: una ricerca
   per parola, non una per lettera. Ogni ricerca resta nel registro degli
   accessi (e' la stessa rotta della pagina), e «r», «ro», «ros» non sono
   ricerche di nessuno. */
const ATTESA_PAZIENTI = 300;

/* Il segno a sinistra di una voce: nel mockup «Comandi» e' un carattere da
   11 px dentro un quadratino da 22. Un'icona da 12 px gli somiglia; fino al
   6/10 erano 14, e il segno pesava piu' del nome accanto. */
const SEGNO = 12;

export default function BarraComandi({ utente, aperta, onApri, onChiudi }) {
    /* Le funzioni del Layout cambiano a ogni disegno: l'ascoltatore della
       tastiera legge le ultime da qui, invece di staccarsi e riattaccarsi. */
    const ultime = useRef({ aperta, onApri, onChiudi });
    ultime.current = { aperta, onApri, onChiudi };

    useEffect(() => {
        const ascolta = (e) => {
            if (!eCtrlK(e)) return;
            const { aperta: giaAperta, onApri: apri, onChiudi: chiudi } = ultime.current;
            /* Aperta, Ctrl K la richiude: e' la stessa scorciatoia, e il
               campo della barra non ha niente da tenersi. */
            if (giaAperta) { e.preventDefault(); chiudi(); return; }
            if (campoTieneCtrlK(e)) return;
            e.preventDefault();
            apri();
        };
        window.addEventListener('keydown', ascolta);
        return () => window.removeEventListener('keydown', ascolta);
    }, []);

    if (!aperta || !utente) return null;
    return createPortal(<Finestra utente={utente} onChiudi={onChiudi} />, document.body);
}

function Finestra({ utente, onChiudi }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [testo, setTesto] = useState('');
    /* La voce scelta: finche' non la si sposta (frecce o mouse) e' la
       migliore per il testo scritto, anche quando arrivano i pazienti; da
       quando la si sposta, resta dove la si e' messa. 'pazienti' vuol dire
       il primo paziente trovato (dopo «Cerca fra i pazienti»). */
    const [scelta, setScelta] = useState(null);
    /* stato: 'vuoto' (niente da cercare) | 'su_richiesta' (il testo e' il
       nome di una schermata: si cerca solo se lo si chiede) | 'cerco' |
       'fatto' | 'errore' */
    const [pazienti, setPazienti] = useState({ stato: 'vuoto', elenco: [], messaggio: null });
    /* Il testo per cui si e' scelto «Cerca fra i pazienti». Vale per quel
       testo solo: scrivendo altro, si torna alla regola di sempre. */
    const [chiesta, setChiesta] = useState(null);
    const campo = useRef(null);
    const elenco = useRef(null);
    const giro = useRef(0);
    /* Dov'era il cursore: ci torna se si chiude senza andare da nessuna
       parte, come dopo qualunque finestra. */
    const prima = useRef(typeof document !== 'undefined' ? document.activeElement : null);
    const andato = useRef(false);

    const puoPazienti = siApre(schermataDi('/pazienti/:id'), utente);
    const azioni = useMemo(() => azioniDi(utente), [utente]);
    const vaiA = useMemo(() => vociVaiA(utente), [utente]);

    useEffect(() => {
        if (campo.current) campo.current.focus();
        return () => {
            const el = prima.current;
            if (!andato.current && el && typeof el.focus === 'function' && document.contains(el)) el.focus();
        };
    }, []);

    /* La ricerca dei pazienti: la stessa chiamata della pagina Pazienti,
       con un limite piu' basso. Una risposta arrivata dopo una piu' nuova
       si butta: conta l'ultima cosa scritta. Sul nome di una schermata non
       parte da sola: aspetta «Cerca fra i pazienti», e allora parte subito. */
    useEffect(() => {
        const q = testo.trim();
        giro.current += 1;
        const questo = giro.current;
        if (!puoPazienti || q.length < MINIMO_PAZIENTI) {
            setPazienti({ stato: 'vuoto', elenco: [], messaggio: null });
            return undefined;
        }
        const suRichiesta = chiesta === q;
        if (!suRichiesta && nomeDiSchermata(q)) {
            setPazienti({ stato: 'su_richiesta', elenco: [], messaggio: null });
            return undefined;
        }
        setPazienti({ stato: 'cerco', elenco: [], messaggio: null });
        const t = setTimeout(async () => {
            try {
                const r = await pazientiAPI.getAll({ search: q, limit: LIMITE_PAZIENTI });
                if (questo !== giro.current) return;
                setPazienti({ stato: 'fatto', elenco: (r.data && r.data.data && r.data.data.pazienti) || [], messaggio: null });
            } catch (e) {
                if (questo !== giro.current) return;
                setPazienti({ stato: 'errore', elenco: [], messaggio: messaggioDa(e, 'Non riesco a cercare i pazienti') });
            }
        }, suRichiesta ? 0 : ATTESA_PAZIENTI);
        return () => clearTimeout(t);
    }, [testo, puoPazienti, chiesta]);

    const { azioni: vociAzioni, vaiA: vociVai } = useMemo(
        () => vociComandi({ testo, azioni, vaiA }), [testo, azioni, vaiA]);
    const vociPazienti = useMemo(() => (pazienti.stato === 'su_richiesta'
        ? [voceCercaPazienti(testo)]
        : pazienti.elenco.map((p) => vocePaziente(p, testo))), [pazienti, testo]);

    const sezioni = [
        { codice: 'azioni', titolo: 'Azioni', voci: vociAzioni },
        { codice: 'vai', titolo: 'Vai a', voci: vociVai },
        { codice: 'pazienti', titolo: 'Pazienti', voci: vociPazienti },
    ].filter((s) => s.voci.length > 0);
    const tutte = sezioni.flatMap((s) => s.voci);
    const primoPaziente = tutte.findIndex((v) => v.tipo === 'paziente');
    let indice = -1;
    if (tutte.length) {
        if (scelta === 'pazienti') indice = primoPaziente >= 0 ? primoPaziente : indiceMigliore(tutte);
        else indice = scelta === null ? indiceMigliore(tutte) : Math.min(scelta, tutte.length - 1);
    }
    const nessunPaziente = pazienti.stato === 'fatto' && chiesta === testo.trim() && pazienti.elenco.length === 0;

    /* Scrivendo, la scelta torna alla voce migliore. */
    useEffect(() => { setScelta(null); }, [testo]);

    /* La voce scelta resta in vista quando si scorre con le frecce. */
    useEffect(() => {
        if (indice < 0 || !elenco.current) return;
        const el = elenco.current.querySelector(`[data-indice="${indice}"]`);
        if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
    }, [indice]);

    const apri = (voce) => {
        if (!voce) return;
        /* «Cerca fra i pazienti»: si cerca, e la barra resta aperta sul
           primo paziente trovato. */
        if (voce.tipo === 'cerca') {
            setChiesta(testo.trim());
            setScelta('pazienti');
            if (campo.current) campo.current.focus();
            return;
        }
        const esito = apriDestinazione(navigate, location, voce);
        if (esito === 'fermato') return;
        andato.current = esito === 'aperto';
        onChiudi();
    };

    const tasto = (e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!tutte.length) return;
            const passo = e.key === 'ArrowDown' ? 1 : -1;
            setScelta((indice + passo + tutte.length) % tutte.length);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (indice >= 0) apri(tutte[indice]);
        } else if (e.key === 'Escape') {
            /* Solo la barra: un'altra finestra aperta sotto non deve
               chiudersi con lo stesso tasto. */
            e.preventDefault();
            e.stopPropagation();
            onChiudi();
        } else if (e.key === 'Tab') {
            /* Il cursore resta nel campo: le voci si scelgono con le
               frecce, e il Tab porterebbe fuori, sulla pagina sotto. */
            e.preventDefault();
        }
    };

    const idVoce = (i) => `bc-voce-${i}`;
    const scorciatoia = eMac() ? '⌘ K' : 'Ctrl K';
    let n = -1;

    return (
        <div className="bc-velo" onMouseDown={(e) => { if (e.target === e.currentTarget) onChiudi(); }}>
            {/* data-barra-comandi: il patto con le prove (scripts/percorsi/guscio.js,
                lo scenario 16 dei percorsi). Le prove cercano la barra per questo
                attributo, il «vai a» per data-tipo="vai" e le azioni per
                data-azione: le classi bc- sono del disegno. */}
            <div className="bc-finestra" role="dialog" aria-modal="true" aria-label="Cerca o esegui"
                 data-barra-comandi="">
                <div className="bc-cerca">
                    <Search size={18} strokeWidth={2} aria-hidden="true" className="bc-lente" />
                    <input
                        ref={campo}
                        className="bc-campo"
                        type="text"
                        role="combobox"
                        aria-label="Cerca una schermata, un'azione o un paziente"
                        aria-expanded="true"
                        aria-controls="bc-elenco"
                        aria-autocomplete="list"
                        aria-activedescendant={indice >= 0 ? idVoce(indice) : undefined}
                        placeholder="Cerca una schermata, un'azione o un paziente"
                        autoComplete="off"
                        spellCheck={false}
                        /* E' una casella di ricerca: non trattiene la
                           ricarica automatica (utils/versioneInLinea.js),
                           come le altre. Chiusa la barra, se ne va comunque. */
                        data-non-trattiene=""
                        value={testo}
                        onChange={(e) => setTesto(e.target.value)}
                        onKeyDown={tasto}
                    />
                    <kbd className="bc-tasto">Esc</kbd>
                </div>
                <div className="bc-elenco" id="bc-elenco" role="listbox" aria-label="Risultati" ref={elenco}>
                    {sezioni.map((s) => (
                        <div key={s.codice} role="group" aria-labelledby={`bc-sezione-${s.codice}`} data-sezione={s.codice}>
                            <div className="bc-sezione" id={`bc-sezione-${s.codice}`}>{s.titolo}</div>
                            {s.voci.map((v) => {
                                n += 1;
                                const i = n;
                                return (
                                    <div
                                        key={v.chiave}
                                        id={idVoce(i)}
                                        role="option"
                                        aria-selected={i === indice}
                                        className={`bc-voce${i === indice ? ' bc-voce--scelta' : ''}`}
                                        data-indice={i}
                                        data-tipo={v.tipo}
                                        data-percorso={v.percorso}
                                        data-azione={v.codice || undefined}
                                        data-paziente={v.id || undefined}
                                        onMouseMove={() => { if (i !== indice) setScelta(i); }}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => apri(v)}
                                    >
                                        <span className={`bc-segno bc-segno--${v.tipo}`} aria-hidden="true">
                                            {v.tipo === 'azione' && <Plus size={SEGNO} strokeWidth={2.2} />}
                                            {v.tipo === 'vai' && <ArrowRight size={SEGNO} strokeWidth={2.2} />}
                                            {v.tipo === 'paziente' && v.iniziali}
                                            {v.tipo === 'cerca' && <UserSearch size={SEGNO} strokeWidth={2.2} />}
                                        </span>
                                        <span className="bc-etichetta">{v.etichetta}</span>
                                        {v.suggerimento && (
                                            <span className={`bc-suggerimento${v.tipo === 'paziente' ? ' bc-codice' : ''}`}>
                                                {v.suggerimento}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                    {pazienti.stato === 'cerco' && (
                        <div className="bc-stato" role="status">Cerco fra i pazienti…</div>
                    )}
                    {pazienti.stato === 'errore' && (
                        <div className="bc-stato bc-stato--errore" role="status">{pazienti.messaggio}</div>
                    )}
                    {nessunPaziente && tutte.length > 0 && (
                        <div className="bc-stato" role="status">Nessun paziente trovato</div>
                    )}
                    {tutte.length === 0 && pazienti.stato !== 'cerco' && pazienti.stato !== 'errore' && (
                        <div className="bc-stato" role="status">Nessun risultato</div>
                    )}
                </div>
                {/* Il promemoria dei tasti, come nel mockup «Comandi»: «⏎» e non
                    «Invio», il segno che sta sul tasto. Per chi usa un lettore
                    di schermo la riga non c'e' (aria-hidden): i tasti glieli
                    dice il campo, che e' una combobox. */}
                <div className="bc-piede" aria-hidden="true">
                    <span><kbd className="bc-piede-tasto">↑ ↓</kbd> per muoverti</span>
                    <span><kbd className="bc-piede-tasto">⏎</kbd> per aprire</span>
                    <span className="bc-spazio" />
                    <span><kbd className="bc-piede-tasto">{scorciatoia}</kbd> da qualsiasi schermata</span>
                </div>
            </div>
        </div>
    );
}
